import { Box, FloatingHelper, Text, TextButton } from '@wix/design-system'
import { useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import React, { FC, useState } from 'react'
import { useMediaQuery } from 'react-responsive'

import { usePageLogger } from '../../../bi/mfi'
import { LARGE_DESKTOP } from '../../../breakpoints'
import { SidePanelContent } from '../../reusable/CommonTemplate/types'
import { RichText } from '../../reusable/RichText'
import { WixStudioImageButtons } from '../../reusable/WixStudioImageButtons/WixStudioImageButtons'
import { WixStudioTemplate } from '../../reusable/WixStudioTemplate/WixStudioTemplate'
import { WixStudioTemplateSidePanel } from '../../reusable/WixStudioTemplate/WixStudioTemplateSidePanel'
import type { Content } from './Content'
import { wixStudioCreateExtendedDataHooks as dataHooks } from './WixStudioCreateExtended.dataHooks'
import s from './WixStudioCreateExtended.scss'

export const WixStudioCreateExtended: FC = () => {
  const [isPageLoading] = usePageLoading()
  const next = useNext()
  const { content } = useContent<Content>()
  const { linksQuestion, defaultLinkQuestion } = content
  const pageLogger = usePageLogger()
  const isLargeScreen = useMediaQuery(LARGE_DESKTOP)
  const [helperOpenedTimes, setHelperOpenedTimes] = useState(0)
  const defaultSidePanel = 'default'
  const [activeSidePanel, setActivePanel] = useState<string>(defaultSidePanel)

  const incrementHelperOpenedTimes = () => setHelperOpenedTimes(prevState => prevState + 1)

  const handleClick = (key: string, value: string) => () => {
    void pageLogger.answer({ answer: value || '' })
    next({ [key]: { values: value ? [value] : [] } })
  }

  const sidePanelsContent: SidePanelContent[] = content.sidePanels.items.map(({ sidePanel }) => sidePanel)

  const activeSidePanelIndex = content.sidePanels.items.findIndex(({ optionKey }) => optionKey === activeSidePanel)

  return (
    <WixStudioTemplate
      isPageLoading={isPageLoading}
      pageLogger={pageLogger}
      title={
        <Box maxWidth={isLargeScreen ? '682px' : '616px'} marginBottom="30px" className={s.WixStudioCreate__title}>
          {content.title}
        </Box>
      }
      sidePanel={
        <WixStudioTemplateSidePanel
          // we need a special treatment for this particular page here
          // because we already have translations for the side panel content.
          content={sidePanelsContent}
          imageMarginBottom={0}
          textMaxWidth={360}
          activeIndex={activeSidePanelIndex !== -1 ? activeSidePanelIndex : undefined}
        />
      }
      bottomNote={
        content.hint && (
          <Box maxWidth="600px" className={s.WixStudioCreate__hint}>
            <RichText size="small" color="#868AA5">
              {content.hint}
            </RichText>
          </Box>
        )
      }
    >
      <Box maxWidth={isLargeScreen ? '682px' : '616px'} direction="vertical" marginBottom="60px">
        {linksQuestion.options.map(option => (
          <WixStudioImageButtons
            key={option.optionKey}
            icon={option.icon}
            title={option.title}
            image={{ src: option.image }}
            description={option.description}
            onClick={handleClick(linksQuestion.questionKey, option.optionKey)}
            dataHook={dataHooks.links({ id: option.optionKey })}
            arrowColor="blue"
          />
        ))}
      </Box>
      <Box gap="SP1" position="absolute" bottom="96px">
        <Text>{defaultLinkQuestion.content.title}</Text>
        {defaultLinkQuestion.options.map(option => (
          <Box key={option.optionKey} verticalAlign="middle" gap="10px">
            <TextButton
              skin={option.color === 'blue' ? 'standard' : 'dark'}
              dataHook={dataHooks.links({ id: option.optionKey })}
              className={s.WixStudioCreate__defaultLinkButton}
              underline="always"
              onClick={handleClick(defaultLinkQuestion.questionKey, option.optionKey)}
              onMouseEnter={() => {
                setActivePanel(option.optionKey)
                if (helperOpenedTimes < 1) {
                  incrementHelperOpenedTimes()
                }
              }}
              onMouseLeave={() => setActivePanel(defaultSidePanel)}
            >
              {option.label}
            </TextButton>
            {content.popover?.content?.isVisible && (
              <FloatingHelper
                opened={helperOpenedTimes === 1}
                target={<span />}
                onClose={incrementHelperOpenedTimes}
                content={
                  <FloatingHelper.Content
                    title={content.popover.content.title}
                    body={
                      <Box direction="vertical">
                        <Text light>{content.popover.content.description}</Text>
                        <TextButton
                          skin="light"
                          underline="always"
                          as="a"
                          target="_blank"
                          href={content.popover.content.url}
                        >
                          {content.popover.content.urlText}
                        </TextButton>
                      </Box>
                    }
                    actionText={content.popover.content.btn}
                    onActionClick={incrementHelperOpenedTimes}
                  />
                }
                placement="right"
              />
            )}
          </Box>
        ))}
      </Box>
    </WixStudioTemplate>
  )
}
